import { identity, intersection, map } from 'lodash';
import type {
  Post as AdapterPost,
  Media,
  Image,
  EmbedMedia,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page/types';
import type { DraftPost } from '@wix/ambassador-blog-v3-draft/types';
import type { Post } from '@wix/ambassador-blog-v3-post/types';
import { getCategoryIds } from '../store/categories/categories-selectors';
import type {
  AppState,
  NormalizedPost,
  PostV2,
  RelatedPostV2,
  ImageMetadata,
  VideoMetadata,
} from '../types';

type Origin = '/v3/posts';

type NormalizePostsParams = {
  posts: AdapterPost[] | Post[] | PostV2[] | undefined;
  blogCategoryIds: string[];
  origin?: Origin;
};

export const normalizePosts = ({
  posts,
  blogCategoryIds,
  origin,
}: NormalizePostsParams): NormalizedPost[] =>
  posts?.map((post) => normalizePost({ post, blogCategoryIds, origin })) ?? [];

type NormalizePostParams = {
  post: RelatedPostV2 | PostV2 | Post | DraftPost | AdapterPost;
  blogCategoryIds: string[];
  origin?: Origin;
};

export const normalizePost = ({
  post,
  blogCategoryIds,
  origin,
}: NormalizePostParams): NormalizedPost => {
  const normalizerFn = getPostNormalizerFn(origin);

  const normalized = {
    ...normalizerFn(post as any),
    categoryIds: normalizeCategoryIds(post, blogCategoryIds),
  } as NormalizedPost;
  const draft = normalized.draft;

  if (isDemoPostHeroImage(normalized.heroImage)) {
    normalized.heroImage = undefined;
  }

  if (draft) {
    draft.categoryIds = normalizeCategoryIds(draft, blogCategoryIds);
    draft.firstPublishedDate ??= 'firstPublishedDate' in post ? post.firstPublishedDate : undefined;
  }

  return normalized;
};

export const normalizePostEntities = (entities: any[], state: AppState) =>
  map(entities, (entity) =>
    entity.type === 'post'
      ? normalizePost({
          post: entity,
          blogCategoryIds: getCategoryIds(state),
        })
      : entity,
  );

type PostSlugSource = {
  slug?: string | null;
  seoSlug?: string | null;
  slugs?: string[] | null;
};

export const getNormalizedPostSlug = (post: PostSlugSource): string | undefined =>
  post.slug ?? post.seoSlug ?? post.slugs?.[0] ?? undefined;

export const normalizePostV3 = (post: Post | AdapterPost): NormalizedPost => {
  const {
    metrics,
    pinned,
    featured,
    mostRecentContributorId,
    pricingPlanIds,
    minutesToRead,
    commentingEnabled,
  } = post;

  const resolvedSlug = getNormalizedPostSlug(post);
  const content = post.richContent || post.content;

  return {
    ...post,
    // v3-post and the frontend adapter generate nominally distinct moderation
    // status enums with identical wire values — cast is safe.
    moderationDetails: post.moderationDetails as NormalizedPost['moderationDetails'],
    content: typeof content === 'string' ? JSON.parse(content) : content,
    coverImage: normalizePostCoverImage(post),
    firstPublishedDate: post.firstPublishedDate as Date,
    likeCount: metrics?.likes || 0,
    viewCount: metrics?.views || 0,
    totalComments: metrics?.comments || 0,
    averageRating: metrics?.averageRating || 0,
    totalRatings: metrics?.totalRatings || 0,
    isFeatured: featured ?? false,
    isPinned: pinned ?? false,
    isCommentsDisabled: !commentingEnabled,
    lastWriterSiteMemberId: mostRecentContributorId,
    paidPlansGroupIds: pricingPlanIds ?? [],
    timeToRead: minutesToRead ?? 0,
    slug: resolvedSlug,
    slugs: resolvedSlug ? [resolvedSlug] : [],
    status: 'published',
    canSeePaidContent: typeof post.preview === 'boolean' ? !post.preview : true,
    ...makeSeoProps(post),
    seoSlug: resolvedSlug,
  };
};

export const getOwnerTitle = (post: PostV2) => post?.owner?.name?.trim();

export const discardDeletedCategoryIds = (postCategoryIds: string[], blogCategoryIds: string[]) =>
  intersection(postCategoryIds || [], blogCategoryIds);

const getPostNormalizerFn = (origin: Origin | undefined) => {
  switch (origin) {
    case '/v3/posts':
      return normalizePostV3;
    default:
      return identity;
  }
};

const normalizeCategoryIds = (
  post: RelatedPostV2 | PostV2 | Post | AdapterPost | DraftPost | undefined,
  blogCategoryIds: string[],
): string[] =>
  post && 'categories' in post && post.categories?.length
    ? post.categories.map((c) => c.id)
    : discardDeletedCategoryIds(post?.categoryIds || [], blogCategoryIds);

const normalizePostCoverImage = (post: AdapterPost | Post): NormalizedPost['coverImage'] => {
  if (post.media?.wixMedia || post.media?.embedMedia) {
    return handlePostMedia(post.media);
  }

  return undefined;
};

const getImageMetadata = (image: Image | undefined): { imageMetadata: ImageMetadata } | undefined =>
  image && {
    imageMetadata: {
      file_name: image.id || image.filename,
      height: image.height ?? 0,
      width: image.width ?? 0,
    },
  };

const getVideoMetadata = (
  video: EmbedMedia | undefined,
): { videoMetadata: VideoMetadata } | undefined => {
  if (!video) {
    return undefined;
  }

  return {
    videoMetadata: {
      video_url: video.video?.url ?? '',
      thumbnail_height: video?.thumbnail?.height ?? 0,
      thumbnail_width: video?.thumbnail?.width ?? 0,
      thumbnail_url: video?.thumbnail?.url ?? '',
    },
  };
};

const handlePostMedia = (media: Media): NormalizedPost['coverImage'] => {
  const { custom, displayed, wixMedia, embedMedia, altText } = media;
  const src =
    wixMedia?.image?.url ||
    wixMedia?.videoV2?.url ||
    embedMedia?.thumbnail?.url ||
    wixMedia?.videoV2?.posters?.[0]?.url ||
    '';
  const commonParams = {
    isCustom: custom ?? false,
    isEnabled: displayed ?? false,
    isResolved: !custom,
    shouldRender: displayed ?? false,
    src,
    altText,
  };

  if (wixMedia?.videoV2) {
    return {
      ...commonParams,
      videoMetadata: {
        video_url: (wixMedia?.videoV2?.url || wixMedia.videoV2.resolutions?.[0]?.url) ?? '',
        thumbnail_height: wixMedia?.videoV2?.height || wixMedia.videoV2.resolutions?.[0]?.height,
        thumbnail_width: wixMedia?.videoV2?.width || wixMedia.videoV2?.resolutions?.[0]?.width,
        thumbnail_url: wixMedia?.videoV2?.posters?.[0]?.url ?? '',
      },
    };
  }

  return {
    ...commonParams,
    ...getImageMetadata(wixMedia?.image),
    ...getVideoMetadata(embedMedia),
  };
};

const makeSeoProps = (post: Post | AdapterPost) => {
  let canonicalUrl: string | undefined;
  let seoTitle: string | undefined;
  let seoDescription: string | undefined;

  let seoShowInSearch = true;
  let seoShowSnippetInSearch = true;

  post.seoData?.tags?.forEach(({ type, props, children }) => {
    switch (type) {
      case 'link':
        if (props?.rel === 'canonical') {
          canonicalUrl = props.href;
        }
        break;
      case 'title':
        seoTitle = children;
        break;
      case 'meta':
        if (props?.name === 'description') {
          seoDescription = props.content;
        } else if (props?.name === 'robots') {
          seoShowInSearch = !props.content?.includes('noindex');
          seoShowSnippetInSearch = !props.content?.includes('nosnippet');
        }
        break;
      default:
        break;
    }
  });

  return {
    canonicalUrl,
    seoTitle,
    seoDescription,
    seoShowInSearch,
    seoShowSnippetInSearch,
    seoSlug: post.slug,
  };
};

export const isDemoPostHeroImage = (
  image: Post['heroImage'] | AdapterPost['heroImage'],
): boolean => {
  if (!image) {
    return false;
  }

  const HERO_IMAGE_IDs = [
    '6d8832_639454ad77914665966c5a0adbdc1bc1~mv2.jpg',
    '6d8832_b4044962b09243ff9cd65c370e5a4357~mv2.jpg',
    '6d8832_84cf8dd687d54912a20640d298afb3d3~mv2.jpg',
  ];

  return HERO_IMAGE_IDs.includes(image.id as string);
};

type GenericMetrics = {
  averageRating?: number;
  comments?: number;
  likes?: number;
  totalRatings?: number;
  views?: number;
};

type GenericPost = {
  id?: string;
  isLiked?: boolean;
};

export function enhancePostsWithMetadata<
  PostType extends GenericPost,
  MetricsType extends GenericMetrics,
>({
  posts,
  postMetricsMap,
  postLikesMap,
}: {
  posts: PostType[];
  postMetricsMap: Record<string, MetricsType>;
  postLikesMap: Record<string, boolean>;
}): PostType[] {
  const defaultMetrics = {
    averageRating: 0,
    comments: 0,
    likes: 0,
    totalRatings: 0,
    views: 0,
  };

  return posts.map((post) => ({
    ...post,
    metrics: (post.id && postMetricsMap[post.id]) || defaultMetrics,
    isLiked: (post.id && postLikesMap[post.id]) || post.isLiked,
  }));
}
